import type { Message } from "../types";

/**
 * Beyond Sure — answer meta footer (L04).
 *
 * Renders the live answer-envelope signals under an assistant turn:
 *   - forced-Strict badge  (server enforced Strict on a financial-intent query)
 *   - mode chip            (strict / creative) when not forced
 *   - confidence meter      (score bar + label, coloured by band)
 *   - honest gate badge     ("Awaiting AI key") when the backend returned available:false
 *
 * Purely presentational — reads the optional fields added to `Message` in L04a. Returns null when the
 * message carries no envelope signal (e.g. a mock/system turn), so it is safe to render unconditionally.
 */

const CONF_COLOR: Record<string, string> = {
  high: "var(--color-brand-deep)",
  medium: "#b8860b",
  low: "var(--color-ink-40)",
};

export function AnswerMeta({ message }: { message: Message }) {
  const conf = message.confidence;
  const hasSignal =
    !!conf ||
    message.forcedStrict ||
    !!message.mode ||
    message.available === false ||
    !!message.escalated;
  if (!hasSignal) return null;

  const pct = conf ? Math.round(Math.max(0, Math.min(1, conf.score)) * 100) : 0;
  const confColor = (conf && CONF_COLOR[conf.label]) || "var(--color-ink-40)";

  return (
    <div className="mt-2.5 flex flex-wrap items-center gap-2" data-testid="answer-meta">
      {message.escalated ? (
        <span
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold"
          style={{ backgroundColor: "rgba(184,134,11,0.12)", color: "#946a00" }}
          title={
            message.holding
              ? "A holding answer is shown — a human expert will confirm shortly."
              : "Routed to a human expert for review."
          }
          data-testid="escalation-badge"
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M12 7v5l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {message.holding ? "Expert will confirm" : "Expert review"}
        </span>
      ) : null}
      {message.forcedStrict ? (
        <span
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold"
          style={{ backgroundColor: "var(--color-brand-soft)", color: "var(--color-brand-deep)" }}
          title="Strict mode was enforced for this financial question (anti-hallucination)."
          data-testid="forced-strict-badge"
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M12 3l7 4v5c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V7l7-4z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
          </svg>
          Strict enforced
        </span>
      ) : message.mode ? (
        <span
          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-medium capitalize bg-[var(--color-ink-08)] text-[var(--color-ink-60)]"
          data-testid="mode-chip"
        >
          {message.mode}
        </span>
      ) : null}

      {conf ? (
        <span
          className="inline-flex items-center gap-1.5"
          data-testid="confidence-meter"
          aria-label={`Confidence ${conf.label} (${pct}%)`}
          title={conf.basis || ""}
        >
          <span className="text-[10.5px] text-[var(--color-ink-40)]">Confidence</span>
          <span className="h-1.5 w-16 rounded-full bg-[var(--color-ink-08)] overflow-hidden">
            <span
              className="block h-full rounded-full"
              style={{ width: `${pct}%`, backgroundColor: confColor }}
            />
          </span>
          <span className="text-[10.5px] font-medium capitalize" style={{ color: confColor }}>
            {conf.label}
          </span>
        </span>
      ) : null}

      {message.available === false ? (
        <span
          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-medium"
          style={{ backgroundColor: "rgba(162,59,59,0.10)", color: "#a23b3b" }}
          title={message.reason || "No AI provider configured."}
          data-testid="gate-badge"
        >
          Awaiting AI key
        </span>
      ) : null}
    </div>
  );
}

export default AnswerMeta;
